import { useEffect, useRef, useState } from 'react';
import type { HomeworkAtelierEmbed } from '@/data/homework-v2';

/**
 * AtelierEmbed · встроенная песочница Ательера внутри карточки домашки.
 * Опция 1 / Вариант A (21 JUN 2026 · Genie): homework-v2 = оболочка + захват (surface),
 * Ательер-песочница = «делание». iframe `/atelier.html?screen=&scenario=&sandbox-mode=on&embed=1`
 * в том же origin → общий localStorage `qastudio.v1.*`; студент тестирует, не уходя из домашки.
 * `embed=1` прячет собственный topbar Ательера (его «← STUDIO · ×» тут мёртв — закрывает оболочка).
 *
 * Адаптив = ГИБРИД (Genie pick 21 JUN): Ательер уходит в свой мобильный баннер при
 * innerWidth<=768, поэтому iframe должен быть шире.
 *   - широкое место (ширина хоста >= MIN_INLINE_PX) → iframe инлайн на всю ширину;
 *   - узкое → карточка с кнопкой → модалка-всплывашка (scrim + центр. карточка, ширина = вьюпорт).
 * Ширину меряем ResizeObserver'ом по реальному хосту (а не 100vw — иначе фикс-сайдбар ломает).
 */
const MIN_INLINE_PX = 800; // ниже — модалка (iframe должен быть > 768, иначе чужой mobile-баннер)

export function AtelierEmbed({
  embed,
  title,
  launchOnly = false,
}: {
  embed: HomeworkAtelierEmbed;
  /** Заголовок домашки — прокидываем в Ательер как intro-фолбэк заголовка карточки-задачи. */
  title?: string;
  /**
   * Тикеты-миссии: колонка капнута на ~750px (< порога 800px), инлайн всё равно не влезает,
   * а на брейкпоинте панель схлопывается → парадокс «уже окно = шире песочница». Genie pick
   * (14 AUG): для тикетов всегда карточка → модалка (модалка на всю ширину, выше моб. порога),
   * без авто-инлайна. Домашка (свой адаптив) проп не передаёт.
   */
  launchOnly?: boolean;
}) {
  const { screen, scenarioId, sandbox, intro } = embed;
  const hostRef = useRef<HTMLDivElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  // init: в jsdom/SSR (нет ResizeObserver) — инлайн (тесты видят iframe); в браузере —
  // грубая прикидка по innerWidth, дальше уточняет ResizeObserver по реальной ширине.
  const [wide, setWide] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof ResizeObserver === 'undefined') return true;
    return window.innerWidth >= 1140;
  });
  const [loaded, setLoaded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Меряем ширину хоста (= ширина пакета домашки) → инлайн vs модалка.
  useEffect(() => {
    const el = hostRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? el.clientWidth;
      setWide(w >= MIN_INLINE_PX);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ESC закрывает модалку; фокус — на кнопку закрытия при открытии.
  useEffect(() => {
    if (!modalOpen) return;
    closeBtnRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setModalOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalOpen]);

  const params = new URLSearchParams({ screen });
  if (scenarioId) params.set('scenario', scenarioId);
  if (sandbox) params.set('sandbox-mode', 'on');
  // Intro-фолбэк: когда у домашки нет полного сценария, оверлей внутри песочницы
  // соберёт мини-карточку задачи из этих параметров (закрывает «потерялась в модалке»).
  if (intro) params.set('mintro', intro);
  if (title) params.set('mtitle', title);
  params.set('embed', '1');
  const src = `/atelier.html?${params.toString()}`;

  const renderFrame = (modal: boolean) => (
    <div className={`hwk-atelier-frame${modal ? ' is-modal' : ''}`}>
      {!loaded && (
        <div className="hwk-atelier-load" aria-live="polite">
          Загружаю песочницу…
        </div>
      )}
      <iframe
        src={src}
        title="Песочница Артели"
        onLoad={() => setLoaded(true)}
        loading="lazy"
      />
    </div>
  );

  return (
    <div className="hwk-atelier" data-testid="hwk-atelier" ref={hostRef}>
      <div className="hwk-atelier-head">
        <span className="hwk-atelier-tag">Песочница · Артель</span>
        {sandbox && (
          <span className="hwk-atelier-sb" title="sandbox-режим: в интерфейс зашиты баги">
            sandbox
          </span>
        )}
      </div>
      {intro && <p className="hwk-atelier-intro">{intro}</p>}

      {wide && !launchOnly ? (
        renderFrame(false)
      ) : (
        <div className="hwk-atelier-launch">
          <p>
            {launchOnly
              ? 'Песочница откроется в отдельном окне на всю ширину — поработаешь и вернёшься к заданию, прогресс останется на месте.'
              : 'Песочнице нужно больше ширины, чем сейчас в окне. Открой её во всплывающем окне — поработаешь и вернёшься в домашку, форма останется на месте.'}
          </p>
          <button
            type="button"
            className="hwk-atelier-open"
            onClick={() => {
              setLoaded(false);
              setModalOpen(true);
            }}
          >
            Открыть песочницу
          </button>
        </div>
      )}

      {modalOpen && (
        <div
          className="hwk-atelier-scrim"
          role="presentation"
          onClick={() => setModalOpen(false)}
        >
          <div
            className="hwk-atelier-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Песочница Артели"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="hwk-atelier-modal-bar">
              <span className="hwk-atelier-modal-ttl">Песочница · {scenarioId ?? screen}</span>
              <button
                ref={closeBtnRef}
                type="button"
                className="hwk-atelier-modal-close"
                onClick={() => setModalOpen(false)}
                aria-label="Закрыть песочницу"
              >
                Закрыть
              </button>
            </div>
            {renderFrame(true)}
          </div>
        </div>
      )}
    </div>
  );
}

export default AtelierEmbed;
